import type { TagColor } from "@/shared/components/ui/tag";

export interface CategoryConfig {
  label: string;
  color: TagColor;
  tooltip: string;
  direction: "in" | "out" | "both";
}

export const CATEGORY_DOT_COLORS: Record<string, string> = {
  green: "bg-green-500",
  salmon: "bg-red-400",
  orange: "bg-orange-400",
  blue: "bg-blue-400",
  purple: "bg-purple-400",
  stone: "bg-stone-400",
};

const ESTATE_CATEGORIES: CategoryConfig[] = [
  {
    label: "Account transfer",
    color: "green",
    tooltip: "Money moved from another account owned by the decedent.",
    direction: "in",
  },
  {
    label: "Sale",
    color: "green",
    tooltip: "Money from selling the decedent's belongings, property, or assets.",
    direction: "in",
  },
  {
    label: "Payment to the estate",
    color: "green",
    tooltip: "Money paid to the estate (e.g., final paycheck, insurance, refunds).",
    direction: "in",
  },
  {
    label: "Interest",
    color: "green",
    tooltip: "Interest earned on funds in this account.",
    direction: "in",
  },
  {
    label: "Income & refunds",
    color: "green",
    tooltip: "Income or refund payments received by the estate.",
    direction: "in",
  },
  {
    label: "Funeral costs",
    color: "salmon",
    tooltip: "Funeral, burial, cremation, or memorial expenses.",
    direction: "out",
  },
  {
    label: "Estate expenses",
    color: "orange",
    tooltip: "Costs to manage the estate, like court, legal, or maintenance fees.",
    direction: "out",
  },
  {
    label: "Debt payment",
    color: "salmon",
    tooltip: "Bills or loans owed before passing (e.g., credit cards, medical).",
    direction: "out",
  },
  {
    label: "Taxes",
    color: "blue",
    tooltip: "Final income, property, or estate taxes owed.",
    direction: "out",
  },
  {
    label: "Reimbursements",
    color: "orange",
    tooltip: "Repayments to anyone who paid estate costs out-of-pocket.",
    direction: "out",
  },
  {
    label: "Distributions",
    color: "purple",
    tooltip: "Inheritance payouts to heirs and beneficiaries.",
    direction: "out",
  },
  {
    label: "Tax payment",
    color: "purple",
    tooltip: "Tax payments made from the estate.",
    direction: "out",
  },
  {
    label: "Other",
    color: "stone",
    tooltip: "Any transaction that doesn't fit the categories above.",
    direction: "both",
  },
];

const CATEGORY_BY_NAME = new Map(ESTATE_CATEGORIES.map((c) => [c.label, c]));

// Plaid personal_finance_category primary/detailed → estate category
// See: https://plaid.com/documents/transactions-personal-finance-category-taxonomy.csv
const PFC_MAP: Record<string, string> = {
  // Transfers
  "TRANSFER_IN_ACCOUNT_TRANSFER": "Account transfer",
  "TRANSFER_IN_DEPOSIT": "Account transfer",
  "TRANSFER_IN_SAVINGS": "Account transfer",
  "TRANSFER_OUT_ACCOUNT_TRANSFER": "Account transfer",
  "TRANSFER_OUT_SAVINGS": "Account transfer",
  "TRANSFER_OUT_WITHDRAWAL": "Account transfer",
  "TRANSFER_IN": "Account transfer",
  "TRANSFER_IN_OTHER_TRANSFER_IN": "Account transfer",
  "TRANSFER_OUT": "Account transfer",
  "TRANSFER_OUT_OTHER_TRANSFER_OUT": "Account transfer",

  // Interest / dividends
  "INCOME_INTEREST_EARNED": "Interest",
  "INCOME_DIVIDENDS": "Interest",
  "INTEREST_EARNED": "Interest",

  // Income
  "INCOME_WAGES": "Payment to the estate",
  "INCOME_RETIREMENT_PENSION": "Payment to the estate",
  "INCOME_OTHER_INCOME": "Payment to the estate",
  "INCOME": "Payment to the estate",

  // Refunds
  "TRANSFER_IN_REFUND": "Income & refunds",
  "INCOME_REFUNDS": "Income & refunds",
  "INCOME_TAX_REFUND": "Income & refunds",

  // Loan / debt payments
  "LOAN_PAYMENTS_CREDIT_CARD_PAYMENT": "Debt payment",
  "LOAN_PAYMENTS_CAR_PAYMENT": "Debt payment",
  "LOAN_PAYMENTS_MORTGAGE_PAYMENT": "Debt payment",
  "LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT": "Debt payment",
  "LOAN_PAYMENTS_PERSONAL_LOAN_PAYMENT": "Debt payment",
  "LOAN_PAYMENTS_OTHER_PAYMENT": "Debt payment",
  "LOAN_PAYMENTS": "Debt payment",

  // Taxes
  "GOVERNMENT_AND_NON_PROFIT_TAX_PAYMENT": "Taxes",
  "TAX_PAYMENT": "Taxes",

  // Rent / utilities / services → estate expenses
  "RENT_AND_UTILITIES_RENT": "Estate expenses",
  "RENT_AND_UTILITIES_GAS_AND_ELECTRICITY": "Estate expenses",
  "RENT_AND_UTILITIES_WATER": "Estate expenses",
  "RENT_AND_UTILITIES_INTERNET_AND_CABLE": "Estate expenses",
  "RENT_AND_UTILITIES_TELEPHONE": "Estate expenses",
  "RENT_AND_UTILITIES_OTHER_UTILITIES": "Estate expenses",
  "RENT_AND_UTILITIES": "Estate expenses",
  "HOME_IMPROVEMENT": "Estate expenses",
  "HOME_IMPROVEMENT_CONTRACTORS": "Estate expenses",
  "GENERAL_SERVICES_INSURANCE": "Estate expenses",
  "GENERAL_SERVICES_ACCOUNTING_AND_FINANCIAL_PLANNING": "Estate expenses",
  "GENERAL_SERVICES_LEGAL": "Estate expenses",
  "GENERAL_SERVICES_POSTAGE_AND_SHIPPING": "Estate expenses",
  "GENERAL_SERVICES_STORAGE": "Estate expenses",
  "GENERAL_SERVICES_OTHER_GENERAL_SERVICES": "Estate expenses",
  "GENERAL_SERVICES": "Estate expenses",
  "BANK_FEES_ATM_FEES": "Estate expenses",
  "BANK_FEES_OVERDRAFT_FEES": "Estate expenses",
  "BANK_FEES_OTHER_BANK_FEES": "Estate expenses",
  "BANK_FEES": "Estate expenses",

  // Government
  "GOVERNMENT_AND_NON_PROFIT_GOVERNMENT_DEPARTMENTS_AND_AGENCIES": "Estate expenses",
  "GOVERNMENT_AND_NON_PROFIT_DONATIONS": "Other",
  "GOVERNMENT_AND_NON_PROFIT": "Estate expenses",

  // Medical
  "MEDICAL_DENTAL_CARE": "Debt payment",
  "MEDICAL_EYE_CARE": "Debt payment",
  "MEDICAL_HOSPITALS_AND_CLINICS": "Debt payment",
  "MEDICAL_PHARMACIES_AND_SUPPLEMENTS": "Debt payment",
  "MEDICAL_VETERINARY_SERVICES": "Other",
  "MEDICAL_OTHER_MEDICAL": "Debt payment",
  "MEDICAL": "Debt payment",

  // Food / travel / shopping → Other
  "FOOD_AND_DRINK": "Other",
  "FOOD_AND_DRINK_RESTAURANT": "Other",
  "FOOD_AND_DRINK_COFFEE": "Other",
  "FOOD_AND_DRINK_FAST_FOOD": "Other",
  "FOOD_AND_DRINK_GROCERIES": "Other",
  "FOOD_AND_DRINK_OTHER": "Other",
  "TRAVEL": "Other",
  "TRAVEL_FLIGHTS": "Other",
  "TRAVEL_LODGING": "Other",
  "TRAVEL_RENTAL_CARS": "Other",
  "TRAVEL_GAS_STATIONS": "Other",
  "TRAVEL_OTHER": "Other",
  "TRANSPORTATION": "Other",
  "TRANSPORTATION_RIDE_SHARE": "Other",
  "TRANSPORTATION_TAXIS_AND_RIDE_SHARES": "Other",
  "TRANSPORTATION_BIKES_AND_SCOOTERS": "Other",
  "TRANSPORTATION_PARKING": "Other",
  "TRANSPORTATION_PUBLIC_TRANSIT": "Other",
  "TRANSPORTATION_TAXIS": "Other",
  "TRANSPORTATION_OTHER": "Other",
  "ENTERTAINMENT": "Other",
  "RECREATION": "Other",
  "GENERAL_MERCHANDISE": "Other",
  "GENERAL_MERCHANDISE_CLOTHING_AND_ACCESSORIES": "Other",
  "GENERAL_MERCHANDISE_ELECTRONICS": "Other",
  "GENERAL_MERCHANDISE_GIFTS_AND_NOVELTIES": "Other",
  "GENERAL_MERCHANDISE_OTHER": "Other",
  "PERSONAL_CARE": "Other",
  "PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS": "Other",
};

// Description keyword fallback
const DESCRIPTION_KEYWORDS_INFLOW: [RegExp, string][] = [
  [/interest|intrst|int pymnt/i, "Interest"],
  [/dividend/i, "Interest"],
  [/refund|rebate|cashback/i, "Income & refunds"],
  [/payroll|salary|wage|direct dep/i, "Payment to the estate"],
  [/insurance|ins pmt/i, "Payment to the estate"],
  [/transfer|xfer/i, "Account transfer"],
  [/deposit/i, "Account transfer"],
];

const DESCRIPTION_KEYWORDS_OUTFLOW: [RegExp, string][] = [
  [/credit card|card payment|cc payment|card \d{4}/i, "Debt payment"],
  [/mortgage|loan payment|student loan/i, "Debt payment"],
  [/medical|hospital|pharmacy|doctor|health|dental/i, "Debt payment"],
  [/tax|irs|franchise tax/i, "Taxes"],
  [/funeral|burial|cremation|memorial|mortuary/i, "Funeral costs"],
  [/attorney|lawyer|legal|court|probate/i, "Estate expenses"],
  [/utility|electric|gas\b|water|sewer|trash/i, "Estate expenses"],
  [/insurance|ins pmt/i, "Estate expenses"],
  [/rent\b|hoa/i, "Estate expenses"],
  [/bank fee|service charge|maintenance fee/i, "Estate expenses"],
  [/interest|intrst|int pymnt/i, "Debt payment"],
  [/transfer|xfer/i, "Account transfer"],
  [/distribution|heir|beneficiary/i, "Distributions"],
  [/reimburse/i, "Reimbursements"],
];

export function getCategoryConfig(
  rawCategory: string | null | undefined,
  isInflow?: boolean,
  description?: string,
  originalDescription?: string | null,
): CategoryConfig | null {
  if (!rawCategory && !description && !originalDescription) return null;

  const direction = isInflow ? "in" : "out";

  // 1. Direct estate category match (user-assigned — bypass direction check)
  const direct = rawCategory ? CATEGORY_BY_NAME.get(rawCategory) : undefined;
  if (direct) return direct;

  // 2. Description keyword match (overrides generic Plaid categories)
  const textsToCheck = [description, originalDescription].filter(Boolean) as string[];
  if (textsToCheck.length > 0) {
    const keywords = isInflow ? DESCRIPTION_KEYWORDS_INFLOW : DESCRIPTION_KEYWORDS_OUTFLOW;
    for (const [pattern, category] of keywords) {
      if (textsToCheck.some((t) => pattern.test(t))) {
        return CATEGORY_BY_NAME.get(category) ?? null;
      }
    }
  }

  // 3. Plaid personal_finance_category match
  if (rawCategory) {
    const pfcMatch = PFC_MAP[rawCategory];
    if (pfcMatch) {
      const config = CATEGORY_BY_NAME.get(pfcMatch);
      if (config && (config.direction === "both" || config.direction === direction)) {
        return config;
      }
    }
  }

  // 4. No category assigned and no Plaid match — return null so "Add category" button shows
  return null;
}

export function getEstateCategoriesForDirection(direction: "in" | "out"): CategoryConfig[] {
  return ESTATE_CATEGORIES.filter((c) => c.direction === direction || c.direction === "both");
}
